import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Field, Spinner, SelectMenu } from '../components/ui';
import { ROOF_TYPES } from '../lib/roofTypes';

const SOURCES = [
  { id: 'call', label: 'Call' },
  { id: 'referral', label: 'Referral' },
  { id: 'web', label: 'Web' },
  { id: 'walk_in', label: 'Walk-in' },
];

export default function NewLead() {
  const navigate = useNavigate();
  const { toast, run } = useApp();
  const { data: customers, loading } = useAsync(() => api.get('/customers'), []);

  const [customerId, setCustomerId] = useState('');
  const [source, setSource] = useState('call');
  const [roofType, setRoofType] = useState('');
  const [roofTypeError, setRoofTypeError] = useState('');
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: '', phone: '', email: '',
    address1: '', address2: '', city: '', state: '', zip: '',
    title: '', notes: '',
  });
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));

  // Picking an existing customer hides the new-customer block. The old version
  // hid it with display:none while leaving the inputs `required`, which made
  // the browser silently refuse to submit with nothing shown to explain why.
  // Here the fields simply aren't rendered, so there's nothing to block on.
  const creatingCustomer = !customerId;

  async function onSubmit(e) {
    e.preventDefault();

    // A brand-new customer is two dependent writes — create the customer, then
    // the project referencing the id the server assigns. That chain can't be
    // queued, so say so up front rather than half-saving it.
    if (!customerId && !navigator.onLine) {
      toast('Adding a new customer needs a connection. Pick an existing customer to log this offline.');
      return;
    }

    if (!roofType) {
      setRoofTypeError('Choose the roof type before creating the lead.');
      return;
    }

    setBusy(true);
    await run(async () => {
      let cid = customerId;
      if (!cid) {
        const c = await api.post('/customers', {
          name: f.name, email: f.email, phone: f.phone,
          address1: f.address1, address2: f.address2,
          city: f.city, state: f.state, zip: f.zip,
        });
        cid = c.id;
      }
      const p = await api.post('/projects', {
        customerId: cid, title: f.title, roofType, source, notes: f.notes,
      });
      toast('Lead created');
      navigate(`/project/${p.id}`, { replace: true });
    });
    setBusy(false);
  }

  if (loading) return <Spinner label="Loading customers" />;

  return (
    <>
      <PageHeader title="New Lead" sub="Log an incoming inquiry" back="/dashboard" />
      <div className="view">
        <Link className="btn sm" to="/documents" style={{ alignSelf: 'flex-start' }}>
          Just need an invoice, receipt or proposal? →
        </Link>
        <form className="col lead-form" onSubmit={onSubmit}>
          <Card>
            <SectionTitle>Customer</SectionTitle>
            <div style={{ marginTop: 'var(--s-3)' }}>
              <Field label="Existing customer" id="cust">
                <SelectMenu
                  id="cust"
                  value={customerId}
                  onChange={setCustomerId}
                  options={[
                    { value: '', label: '— Add new customer —' },
                    ...(customers || []).map(c => ({ value: c.id, label: c.name })),
                  ]}
                />
              </Field>
            </div>

            {creatingCustomer ? (
              <div className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }}>
                <Field label="Customer name *" id="name">
                  <input id="name" className="fi" required value={f.name} onChange={set('name')} />
                </Field>
                <div className="row-fields">
                  <Field label="Phone *" id="phone" hint="The most reliable way to reach a lead">
                    <input id="phone" className="fi" type="tel" required value={f.phone} onChange={set('phone')} />
                  </Field>
                  <Field label="Email" id="email" hint="Optional — used to send quotes and invoices">
                    <input id="email" className="fi" type="email" value={f.email} onChange={set('email')} />
                  </Field>
                </div>
                <Field label="Address line 1 *" id="a1">
                  <input id="a1" className="fi" required value={f.address1} onChange={set('address1')} />
                </Field>
                <Field label="Address line 2" id="a2" hint="Apt, suite, unit… (optional)">
                  <input id="a2" className="fi" value={f.address2} onChange={set('address2')} />
                </Field>
                <div className="row-fields" style={{ '--cols': 3 }}>
                  <Field label="City" id="city"><input id="city" className="fi" required value={f.city} onChange={set('city')} /></Field>
                  <Field label="State" id="state"><input id="state" className="fi" required value={f.state} onChange={set('state')} /></Field>
                  <Field label="Zip" id="zip"><input id="zip" className="fi" value={f.zip} onChange={set('zip')} /></Field>
                </div>
              </div>
            ) : null}
          </Card>

          <Card>
            <SectionTitle>Project</SectionTitle>
            <div className="col project-fields">
              <Field label="Project title *" id="title">
                <input id="title" className="fi" required placeholder="e.g. Roof leak inspection" value={f.title} onChange={set('title')} />
              </Field>
              <Field label="Roof type *" id="roof-type" error={roofTypeError}>
                <SelectMenu
                  id="roof-type"
                  value={roofType}
                  onChange={(value) => {
                    setRoofType(value);
                    setRoofTypeError('');
                  }}
                  options={ROOF_TYPES}
                  placeholder="Select roof type"
                />
              </Field>
              <div className="field choice-field">
                <span className="sb" style={{ fontWeight: 600 }}>Source</span>
                <div className="seg" role="group" aria-label="Lead source">
                  {SOURCES.map(s => (
                    <button key={s.id} type="button" aria-pressed={source === s.id} onClick={() => setSource(s.id)}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Notes" id="notes">
                <textarea id="notes" className="fi" rows={3} placeholder="Phone notes, what the customer said…" value={f.notes} onChange={set('notes')} />
              </Field>
            </div>
          </Card>

          <button className="btn pri" type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create lead'}
          </button>
        </form>
      </div>
    </>
  );
}
